// @ts-check
import { APP_VERSION } from '@/js/common/constants';
import { TOTAL_FORMS } from '@/js/common/developerModeForms';
import { useAppConfigStore } from '@/js/stores/appConfig';

import { useSettingRef } from '@erpagroapp/settings';
import { f7 } from 'framework7-vue';
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';

/** @import { ComputedRef, Ref } from 'vue' */

/** @typedef {'username' | 'route' | 'center' | 'appVersion'} InformationBarItemKey */
/** @typedef {'showInformationBar' | `show${Capitalize<InformationBarItemKey>}`} InformationBarItemSettingName */

/**
 * @typedef {object} InformationBarItem
 * @property {string} title - The title of the item.
 * @property {InformationBarItemKey} key - The key of the item.
 * @property {InformationBarItemSettingName} settingName - The name of the setting for the item.
 * @property {string} [icon] - The icon class for the item.
 * @property {string} [text] - The text of the item.
 * @property {string} [hint] - The hint of the item.
 * @property {boolean} [isHidden=false] - Whether the item is hidden.
 * @property {boolean} [clickable=false] - Whether the item is clickable.
 */

export const useInformationBar = () => {
  const appConfig = useAppConfigStore();
  const showInformationBar = useSettingRef('BarraInformacion.MostrarBarra');
  const informationBarOrder = useSettingRef('BarraInformacion.OrdenElementos');

  /** @type {Ref<InformationBarItem[]>} */
  const items = ref([
    {
      title: 'informationBar.username',
      key: 'username',
      settingName: 'showUsername',
      icon: 'fa6-solid-user',
      text: undefined,
      hint: 'informationBar.username',
      isHidden: false,
      clickable: false,
    },
    {
      title: 'informationBar.route',
      key: 'route',
      settingName: 'showRoute',
      icon: 'fa6-solid-house',
      text: undefined,
      hint: 'informationBar.route',
      isHidden: false,
      clickable: false,
    },
    {
      title: 'informationBar.center',
      key: 'center',
      settingName: 'showCenter',
      icon: 'fa6-solid-location-arrow',
      text: undefined,
      hint: 'informationBar.center',
      isHidden: false,
      clickable: false,
    },
    {
      title: 'informationBar.appVersion',
      key: 'appVersion',
      settingName: 'showAppVersion',
      icon: 'fa6-solid-circle-info',
      text: APP_VERSION,
      hint: 'informationBar.appVersion',
      isHidden: false,
      clickable: false,
    },
  ]);

  /** @type {ComputedRef<InformationBarItem[]>} */
  const visibleItems = computed(() => informationBarOrder.value
    .map((key) => items.value.find((item) => item.key === key))
    .filter((item) => typeof item !== 'undefined' && !isItemHidden(item)));

  const configurableItems = computed(() => items.value.filter((item) => item.settingName));

  const leftItems = computed(() => visibleItems.value.filter(
    (_item, index, array) => (index !== array.length - 1 || array.length === 1),
  ));

  const rightItems = computed(() => visibleItems.value.filter(
    (_item, index, array) => (index === array.length - 1 && array.length > 1),
  ));

  const informationBarRefs = /** @satisfies {Record<InformationBarItemKey, Ref<boolean>>} */ ({
    username: useSettingRef('BarraInformacion.MostrarNombreUsuario'),
    route: useSettingRef('BarraInformacion.MostrarRuta'),
    center: useSettingRef('BarraInformacion.MostrarCentro'),
    appVersion: useSettingRef('BarraInformacion.MostrarVersionApp'),
  });

  const shouldShowInformationBar = computed(() => showInformationBar.value
    && items.value.some((item) => informationBarRefs[item.key].value));

  /**
   * Retrieves the route name (`Nombre` prop) based on the given route (`Formulario` prop).
   * @param {string} route - The route to find in the `TOTAL_FORMS` array.
   * @returns {string | undefined} The route name if found, or `undefined` if the route is not found.
   */
  function getRouteNameByForm(route) {
    if (route === '/') {
      return 'Dashboard';
    }

    /**
     * Checks if the given route is valid.
     * @param {Record<string, any>} obj - The object to check.
     * @param {string} prop - The property to check.
     * @returns {boolean} `true` if the route is valid, `false` otherwise.
     */
    const containsRoute = (obj, prop) => route.includes(obj[prop]);

    /** @type {string} */
    const prop = 'Formulario';

    for (const module of TOTAL_FORMS) {
      if (containsRoute(module, prop)) {
        return module.Nombre;
      }

      for (const subModule of module.SubModulos) {
        if (containsRoute(subModule, prop)) {
          return subModule.Nombre;
        }

        for (const process of subModule.Procesos) {
          if (containsRoute(process, prop)) {
            return process.Nombre;
          }
        }
      }

      for (const process of module.Procesos) {
        if (containsRoute(process, prop)) {
          return process.Nombre;
        }
      }
    }

    return undefined;
  }

  function handleInformationBarSettingsChange() {
    for (const item of items.value) {
      item.isHidden = !informationBarRefs[item.key].value;

      watch(() => informationBarRefs[item.key], () => {
        item.isHidden = !informationBarRefs[item.key];
      });
    }
  }

  function handleUsernameChange() {
    const userItem = items.value.find((item) => item.key === 'username');
    if (userItem) {
      userItem.text = appConfig.userLoginData?.UserName;
    }

    watch(() => appConfig.userLoginData, () => {
      if (userItem) {
        const name = appConfig.userLoginData?.UserName ?? '';
        userItem.text = name;
      }
    });
  }

  function handleCenterChange() {
    const centerItem = items.value.find((item) => item.key === 'center');
    if (centerItem) {
      centerItem.text = appConfig.userEnviroment?.EntornoDeTrabajo.Centro?.Nombre ?? '';
    }

    watch(() => appConfig.userEnviroment, () => {
      if (centerItem) {
        const name = appConfig.userEnviroment?.EntornoDeTrabajo.Centro?.Nombre ?? '';
        centerItem.text = name;
      }
    }, { deep: true });
  }

  function handleRouteChange() {
    const routeItem = items.value.find((item) => item.key === 'route');

    const setRouteText = () => {
      if (routeItem) {
        const currentRoute = f7.views.main?.router.currentRoute?.route?.path;

        if (!currentRoute) {
          routeItem.isHidden = true;
        } else {
          const name = getRouteNameByForm(currentRoute);
          routeItem.isHidden = !name;

          if (!routeItem.isHidden) {
            routeItem.text = name;
          }
        }
      }
    };

    if (routeItem) {
      setRouteText();
    }

    f7.on('routeChanged', () => {
      setRouteText();
    });

    f7.on('pageInit', () => {
      setRouteText();
    });
  }

  /**
   * Checks if the item should be hidden.
   * @param {InformationBarItem} item - The item to check.
   * @returns {item is InformationBarItem} `true` if the item should be hidden, `false` otherwise.
   */
  function isItemHidden(item) {
    if (!item) {
      return false;
    }

    return !informationBarRefs[item.key].value || Boolean(item.isHidden);
  }

  onMounted(() => {
    handleInformationBarSettingsChange();
    handleUsernameChange();
    handleCenterChange();
    handleRouteChange();
  });

  return {
    items,
    visibleItems,
    configurableItems,
    leftItems,
    rightItems,
    informationBarRefs,
    shouldShowInformationBar,
  };
};
